/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  Anubis Connector localisation — read this before you add a key.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  const t = useT()
 *  t('common.actions.save')                        → 'Save'      / 'Salva'
 *  t('common.units.posts', { count: n })           → '3 posts'   / '3 post'
 *  t('common.shell.userMenu', { name: user.name }) → 'Account menu for Ada'
 *
 *  const tn = useTNode()                            // when a sentence needs markup inside it
 *  tn('settings.language.storedNote', {
 *    account: (chunk) => <strong>{chunk}</strong>,
 *  })
 *
 *  const f = useFormat()                            // every number, date and currency
 *  f.compact(2_200_000)  ·  f.range(from, to)  ·  f.currencyCents(1800)
 *
 *  CONVENTIONS — all six are enforced by `tsc` or by `npm run i18n:check`:
 *
 *  1. Key naming is `<area>.<component>.<thing>` — `settings.whitelabel.contrastWarning`,
 *     `planner.composer.bestTimeHint`. `common.*` holds only what genuinely recurs: actions,
 *     statuses, units, time words, the shell. Everything else lives with its feature, so a
 *     deleted feature takes its keys with it.
 *
 *  2. Never concatenate. `t('a') + name + t('b')` cannot be translated into a language that
 *     orders the sentence differently. One key, one whole sentence, placeholders inside it.
 *
 *  3. Never split a sentence to get markup into it. A key that needs bold, a link or a chip
 *     in the middle carries a named slot — `Saved to <account>your account</account>` — and
 *     the caller passes `{ account: (chunk) => <strong>{chunk}</strong> }` to `tn()`. Name the
 *     slot after what it *is*, never after how it looks: the translator moves it, the code
 *     styles it.
 *
 *  4. Never build a key at runtime. `t('status.' + s)` defeats the type check and hides the
 *     key from the unused-key scan. Use an explicit `Record<Status, SimpleTKey>` map.
 *
 *  5. Placeholders are named, never positional: `{count}`, `{brandName}`. A number param is
 *     formatted in the active locale automatically. An unknown placeholder renders as
 *     `{name}` so it is impossible to miss in review.
 *
 *  6. Plurals go through `count`, and `Intl.PluralRules` picks the form from the catalogue's
 *     `{ one, other }`. English and Italian differ at zero — *0 posts* / *0 post* — and that
 *     lives in the catalogue, never in an `if`. An extra `zero` form, when present, is used
 *     for exactly zero: *No posts yet*.
 *
 *  Not translated, ever: network names (Facebook, Instagram, TikTok, YouTube, Google Business
 *  Profile), metric keys on the wire, anything a user wrote, and provider error text — we show
 *  what the network said, verbatim, and translate only our framing around it.
 *
 *  Numbers, dates and currency are *formatted*, not translated: `./format.ts`, never by hand.
 *  Weekday and month names come from `weekdayNames()` / `monthNames()` — `Intl` already knows
 *  Italian writes them lowercase.
 *
 *  Adding a key: put it in `en.json` (English is the source), then in `it.json` with the same
 *  name and the same placeholders. `tsc` fails on a missing or misspelled key;
 *  `npm run i18n:check` fails on a placeholder that does not match.
 */

import { Fragment, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'

import { apiV1, getAccessToken } from '../api'
import type { PluralTKey, SimpleTKey, TKey } from './catalog'
import { messageFor } from './catalog'
import { applyLocale, getLocale } from './locale'
import type { Locale, MessageNode, MessageParams, ParamValue } from './shared'
import {
  LOCALE_NATIVE_NAME,
  formatMessage,
  formatParam,
  intlLocaleOf,
  parseMessage,
  resolveMessage,
} from './shared'
import { useActiveLocale } from './useLocaleStore'

/* ── t(): a sentence as a string ──────────────────────────────────────────── */

/**
 * `count` is mandatory on a plural key and rejected nowhere else — that is the whole reason
 * the catalogue stores `{ one, other }` objects instead of two flat keys.
 */
export interface TFunction {
  (key: SimpleTKey, params?: MessageParams): string
  (key: PluralTKey, params: MessageParams & { count: number }): string
}

const translatorFor = (locale: Locale): TFunction => {
  const intlLocale = intlLocaleOf(locale)
  const translate = (key: TKey, params?: MessageParams): string =>
    formatMessage(messageFor(locale, key), intlLocale, params)
  return translate
}

/** The one hook every screen uses. Works with or without `LocaleProvider` above it. */
export const useT = (): TFunction => {
  const locale = useActiveLocale()
  return useMemo(() => translatorFor(locale), [locale])
}

/* ── tn(): a sentence with markup in the middle of it ─────────────────────── */

/** A slot's value: something to render, or a function that wraps the slot's own text. */
export type TNodeParam = ReactNode | ((chunk: ReactNode) => ReactNode)

export type TNodeParams = Readonly<Record<string, TNodeParam>>

export interface TNodeFunction {
  (key: SimpleTKey, params?: TNodeParams): ReactNode
  (key: PluralTKey, params: TNodeParams & { count: number }): ReactNode
}

const isRenderer = (value: TNodeParam): value is (chunk: ReactNode) => ReactNode =>
  typeof value === 'function'

const renderNodes = (
  nodes: readonly MessageNode[],
  intlLocale: string,
  params: TNodeParams | undefined,
): ReactNode[] =>
  nodes.map((node, index) => {
    const key = `${node.type}-${index}`

    if (node.type === 'text') return <Fragment key={key}>{node.value}</Fragment>

    if (node.type === 'tag') {
      const children = renderNodes(node.children, intlLocale, params)
      const slot = params?.[node.name]
      // No renderer for this slot: keep the sentence, drop the markers. A translator's tag can
      // never blank a line.
      if (slot === undefined || !isRenderer(slot)) return <Fragment key={key}>{children}</Fragment>
      return <Fragment key={key}>{slot(children)}</Fragment>
    }

    const value = params?.[node.name]
    if (value === undefined) return <Fragment key={key}>{`{${node.name}}`}</Fragment>
    if (isRenderer(value)) return <Fragment key={key}>{value(null)}</Fragment>
    if (typeof value === 'number' || typeof value === 'string') {
      return <Fragment key={key}>{formatParam(value, intlLocale)}</Fragment>
    }
    return <Fragment key={key}>{value}</Fragment>
  })

const nodeTranslatorFor = (locale: Locale): TNodeFunction => {
  const intlLocale = intlLocaleOf(locale)
  const translate = (key: TKey, params?: TNodeParams): ReactNode => {
    const count = params?.['count']
    const forSelection: MessageParams | undefined =
      typeof count === 'number' ? { count } : undefined
    const template = resolveMessage(messageFor(locale, key), intlLocale, forSelection)
    return <>{renderNodes(parseMessage(template), intlLocale, params)}</>
  }
  return translate
}

/**
 * The sentence-with-markup variant. Same keys, same plurals; the difference is that a slot
 * can become an element. Never ask a translator to split a sentence in two.
 */
export const useTNode = (): TNodeFunction => {
  const locale = useActiveLocale()
  return useMemo(() => nodeTranslatorFor(locale), [locale])
}

/* ── changing the language ────────────────────────────────────────────────── */

/**
 * The user chose a language. Writes all three layers in one action: the render and the
 * `pd.locale` mirror immediately, then `User.locale` through `PATCH /api/v1/users/me` — which
 * is what makes the choice survive logout, another browser and another machine.
 *
 * The account write only happens when there is a session; signed out, the choice still stands
 * and still survives a reload. It rejects with the `ApiError` *after* the interface has
 * already changed: sitting in the old language because a request failed would be the worse
 * failure. Callers report it with `common.locale.changeFailed`.
 */
export const changeLocale = async (locale: Locale): Promise<void> => {
  applyLocale(locale)
  if (getAccessToken() === null) return
  await apiV1('/users/me', { method: 'PATCH', json: { locale } })
}

/* ── the provider ─────────────────────────────────────────────────────────── */

export interface LocaleContextValue {
  locale: Locale
  /** The BCP-47 tag `Intl` gets — `en-GB` / `it-IT`. */
  intlLocale: string
  /** `Italiano` / `English` — a language is always labelled in its own language. */
  nativeName: string
  /**
   * Applies the language, mirrors it to `pd.locale`, sets `<html lang>` and, when there is a
   * session, saves it to `User.locale`. Never rejects: a failed account write surfaces as
   * `saveError` while the interface has already changed.
   */
  setLocale: (locale: Locale) => void
  /** True while the account write is in flight. */
  isSaving: boolean
  /** The last account-write failure. Cleared by the next successful change. */
  saveError: unknown
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

/**
 * Mount it once, above everything that renders copy — the app shell, the auth screens and the
 * marketing site all carry the switcher. `useT`, `useTNode`, `useFormat` and `useLocale` all
 * read the store underneath and keep working if it is missing; the provider adds the saving
 * state and re-asserts `<html lang>`.
 */
export const LocaleProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const locale = useActiveLocale()
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<unknown>(null)

  useEffect(() => {
    // The store already did this at module load; this keeps it true after a hot reload or a
    // locale change that came from the session rather than from the switcher.
    applyLocale(getLocale())
  }, [locale])

  const setLocale = useCallback((next: Locale): void => {
    setIsSaving(true)
    setSaveError(null)
    void changeLocale(next)
      .then(() => {
        setSaveError(null)
      })
      .catch((error: unknown) => {
        setSaveError(error)
      })
      .finally(() => {
        setIsSaving(false)
      })
  }, [])

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      intlLocale: intlLocaleOf(locale),
      nativeName: LOCALE_NATIVE_NAME[locale],
      setLocale,
      isSaving,
      saveError,
    }),
    [locale, setLocale, isSaving, saveError],
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

/**
 * The active locale and the way to change it.
 *
 * Outside a `LocaleProvider` it still works — the store is the source of truth — it simply
 * cannot report the saving state. That is deliberate: a switcher on a marketing page must not
 * depend on where a provider happens to be mounted.
 */
export const useLocale = (): LocaleContextValue => {
  const context = useContext(LocaleContext)
  const locale = useActiveLocale()

  const fallback = useMemo<LocaleContextValue>(
    () => ({
      locale,
      intlLocale: intlLocaleOf(locale),
      nativeName: LOCALE_NATIVE_NAME[locale],
      setLocale: (next: Locale) => {
        void changeLocale(next).catch(() => {
          // Without the provider there is nowhere to report it; the local change still stands.
        })
      },
      isSaving: false,
      saveError: null,
    }),
    [locale],
  )

  return context ?? fallback
}

/* ── surface ──────────────────────────────────────────────────────────────── */

export type { Locale, MessageParams, ParamValue }
export type { PluralTKey, SimpleTKey, TKey }
export { LOCALES, LOCALE_NATIVE_NAME, isLocale, normalizeLocale } from './shared'
export { LOCALE_STORAGE_KEY, adoptSessionLocale, getLocale } from './locale'
export { useActiveLocale } from './useLocaleStore'
export {
  formattersFor,
  formatCompactNumber,
  formatCurrency,
  formatCurrencyCents,
  formatDate,
  formatDateRange,
  formatDateTime,
  formatDecimal,
  formatDuration,
  formatInteger,
  formatList,
  formatNumber,
  formatPercent,
  formatRelativeTime,
  formatSigned,
  formatTime,
  monthNames,
  useFormat,
  weekdayNames,
} from './format'
export type { DateInput, Formatters, NameWidth, ZoneOption } from './format'
